·<template>
  <div class="container">
    <!-- 全局导航遮罩，防止重复点击 -->
    <div v-if="navigating" class="navigating-overlay">
      <div class="navigating-box">
        <i class="fas fa-spinner fa-spin"></i> 正在跳转...
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="fas fa-spinner fa-spin"></i> 加载中...
    </div>

    <template v-else>
      <!-- 导航栏 -->
      <div class="navbar">
        <div class="logo-area">
          <div class="brand">Faculty Style</div>
        </div>
        <div class="nav-links">
          <a href="#" @click.prevent="$router.push('/dashboard')"><i class="fas fa-hiking"></i> 首页</a>
          <a href="#" @click.prevent="showMiniProgram">小程序</a>
          <a href="#" @click.prevent="$router.push('/about')">关于我们</a>
          <a href="#" class="btn-outline-light">联系客服 <i class="fas fa-headset"></i></a>
          <a href="#" @click.prevent="$router.push('/profile')"><i class="far fa-user-circle"></i> 我的</a>
        </div>
      </div>

      <!-- Hero 区域 -->
      <div class="hero">
        <div class="subhead"><i class="fas fa-chalkboard-teacher"></i> 致敬中国教育先锋 · 以专业引领未来</div>
        <h1>教师风采 <span style="color:#1a5a36;">|</span> 真实名家·榜样力量</h1>
        <p>本页展示我司认证教练，涵盖攀树、溪降等多项户外技能。</p>
      </div>

      <!-- 教师网格 -->
      <div class="teacher-grid">
        <TeacherCard
          v-for="teacher in teachers"
          :key="teacher.id"
          :teacher="teacher"
          :disabled="navigating"
          @book="handleBook"
        />
      </div>

      <!-- 额外功能区域 -->
      <div class="extra-features">
        <div class="feature-link"><i class="fas fa-chalkboard-teacher"></i> 教师风采 · 认证教练团队</div>
        <div class="feature-link" @click="showMiniProgram"><i class="fas fa-qrcode"></i> 报名参加 · 小程序快捷报名</div>
        <div class="feature-link" @click="$router.push('/about')"><i class="fas fa-building"></i> 关于我们 · 学术合作机构</div>
        <div class="feature-link contact-btn"><i class="fas fa-headset"></i> 联系客服 · 导师咨询</div>
      </div>

      <!-- 名师公开课区域（静态数据，暂不从接口获取） -->
      <div class="inspire-section">
        <div class="inspire-title">
          <i class="fas fa-calendar-check" style="color:#1a5a36;"></i> 名师公开课 & 工作坊 <span style="font-size:0.9rem; font-weight:normal;">🔥 真实日程·敬请关注</span>
        </div>
        <div class="mini-cards">
          <div class="mini-card">
            <i class="fas fa-tree"></i>
            <div><strong>攀树技巧实训课</strong><br><span style="font-size:0.7rem;">徐潇 · 高阶教练</span></div>
            <div class="status-badge" style="margin-left:auto; cursor:pointer;" @click="showMiniProgram">报名</div>
          </div>
          <div class="mini-card">
            <i class="fas fa-water"></i>
            <div><strong>溪降技术入门</strong><br><span style="font-size:0.7rem;">徐潇 · 初阶教练</span></div>
            <div class="status-badge" style="margin-left:auto; cursor:pointer;" @click="showMiniProgram">报名</div>
          </div>
          <div class="mini-card">
            <i class="fas fa-shield-alt"></i>
            <div><strong>个人防护器材（PPE）检查与保养</strong><br><span style="font-size:0.7rem;">吕军 · 初阶教练</span></div>
            <div class="status-badge" style="margin-left:auto; cursor:pointer;" @click="showMiniProgram">报名</div>
          </div>
          <div class="mini-card">
            <i class="fas fa-hard-hat"></i>
            <div><strong>个人防护器材（PPE）基础实操</strong><br><span style="font-size:0.7rem;">單浩 · 初阶教练</span></div>
            <div class="status-badge" style="margin-left:auto; cursor:pointer;" @click="showMiniProgram">报名</div>
          </div>
        </div>
      </div>

      <div class="footer-note"></div>
    </template>
  </div>
</template>

<script>
import TeacherCard from '../components/TeacherCard.vue'
import '@/assets/css/global.css'
import { getTeacherList } from '@/api/teacher'
import { getFeaturedList } from '@/api/featured'
import { getActivityDetail } from '@/api/booking'
import { getCoachCourses, getCourseStudents, getCoachQualifications } from '@/api/coach'
import { jumpToMiniApp } from '@/config'
import { eventBus } from '@/utils/eventBus'

export default {
  name: 'Index',
  components: {
    TeacherCard
  },
  data() {
    return {
      teachers: [],
      loading: false,
      navigating: false  // 防止重复导航
    }
  },
  created() {
    this.fetchTeachers()
  },
  methods: {
    // 获取教师列表：优先展示精选教师，无精选则回退到所有教师
    async fetchTeachers() {
      this.loading = true
      try {
        // 先获取精选展示列表
        const featuredRes = await getFeaturedList()
        if (featuredRes && featuredRes.code === 200 && featuredRes.data && featuredRes.data.length > 0) {
          this.teachers = featuredRes.data.map(item => this.formatTeacherData(item))
          return
        }

        // 精选列表为空，回退到所有教师
        const res = await getTeacherList()
        if (res && res.code === 200) {
          const teacherList = res.data || []
          this.teachers = teacherList.map(item => this.formatTeacherData(item))
        } else {
          console.warn('接口返回异常:', res)
          this.teachers = []
        }
      } catch (error) {
        console.error('获取教师列表失败:', error)
        this.teachers = []
      } finally {
        this.loading = false
        // 教师加载完成后，异步获取课程数、学员数和资质等级
        this.loadTeachersStats()
      }
    },

    // 异步加载每个教师的课程数和学员数
    async loadTeachersStats() {
      this.teachers.forEach((_, i) => this.loadTeacherStats(i))
    },

    async loadTeacherStats(index) {
      const teacher = this.teachers[index]
      if (!teacher) return
      // 加载资质等级
      try {
        const qRes = await getCoachQualifications(teacher.id)
        if (qRes && qRes.code === 200 && Array.isArray(qRes.data)) {
          teacher.qualifications = qRes.data.map(q => ({
            name: q.categoryName || '',
            level: q.levelName || ''
          }))
        }
      } catch (e) { /* 忽略 */ }
      // 加载课程和学员数
      try {
        const res = await getCoachCourses(teacher.id)
        if (res && res.code === 200 && Array.isArray(res.data)) {
          const courses = res.data
          teacher.courseCount = courses.length
          const counts = await Promise.all(
            courses.map(c =>
              getCourseStudents(c.id || c.courseId)
                .then(sres => ((sres && sres.data) || []).length)
                .catch(() => 0)
            )
          )
          teacher.studentCount = counts.reduce((sum, c) => sum + c, 0)
        }
      } catch (error) {
        console.error('加载教师统计数据失败:', error)
      }
    },

    // 格式化接口返回的教师数据，适配前端组件
    formatTeacherData(item) {
      return {
        id: item.userId || item.id,
        name: item.userName || item.name,
        gender: item.gender,
        title: item.title || item.position,
        bio: item.bio || item.description || item.introduction,
        expertise: item.expertiseTags || [],
        stats: this.formatStats(item),
        status: {
          text: item.status || '已成行',
          type: this.getStatusType(item.status)
        },
        imgClass: `img-${item.userId || item.id}`,
        imgTag: '',
        avatarUrl: this.getAvatarUrl(item.avatar_url || item.avatarUrl),
        qualifications: [],
        courseCount: 0,
        studentCount: 0,
        buttonText: 'ta 的带队活动',
        buttonIcon: 'fas fa-arrow-right'
      }
    },

    getAvatarUrl(avatarFilename) {
      if (!avatarFilename) return ''
      return avatarFilename.startsWith('/api/') ? avatarFilename : avatarFilename.replace('/uploads/', '/api/uploads/')
    },

    // 格式化统计数据
    formatStats(item) {
      // 如果后端直接返回了 stats 数组
      if (item.stats && Array.isArray(item.stats)) {
        return item.stats
      }

      // 从单独的字段组装
      const stats = []
      if (item.achievements) {
        stats.push({ icon: 'fas fa-trophy', text: item.achievements })
      }
      if (item.years_of_experience) {
        stats.push({ icon: 'fas fa-calendar-alt', text: `${item.years_of_experience}年经验` })
      }
      // 课程数和学员数将在 loadTeacherStats 完成后通过响应式更新显示
      return stats
    },

    // 获取状态类型
    getStatusType(status) {
      if (!status) return 'success'
      if (status.includes('满员') || status.includes('已满')) return 'full'
      if (status.includes('紧张') || status.includes('余位')) return 'warning'
      return 'success'
    },

    // 处理预约 - 接收完整的teacher对象
    async handleBook(teacher) {
      // 防止快速重复点击
      if (this.navigating) return
      this.navigating = true

      const teacherId = teacher.id
      if (!teacherId) {
        this.$message({
          message: '教师信息不完整，无法获取活动列表',
          type: 'warning',
          duration: 2000
        })
        this.navigating = false
        return
      }

      try {
        const res = await getActivityDetail(teacherId)
        const courseData = res && res.code === 200 ? res.data : []

        const teacherInfo = {
          id: teacher.id,
          name: teacher.name,
          title: teacher.title
        }
        localStorage.setItem('selectedTeacherForBooking', JSON.stringify(teacherInfo))
        localStorage.setItem('teacherActivities', JSON.stringify(courseData || []))

        await this.$router.push({
          path: '/booking',
          query: { teacherId, teacherName: teacher.name }
        }).catch(() => {})
      } catch (error) {
        console.error('获取课程失败:', error)
        // 即使失败也跳转，显示暂无课程
        const teacherInfo = {
          id: teacher.id,
          name: teacher.name,
          title: teacher.title
        }
        localStorage.setItem('selectedTeacherForBooking', JSON.stringify(teacherInfo))
        localStorage.setItem('teacherActivities', JSON.stringify([]))
        await this.$router.push({
          path: '/booking',
          query: { teacherId, teacherName: teacher.name }
        }).catch(() => {})
      } finally {
        this.navigating = false
      }
    },

    // 显示小程序
    showMiniProgram() {
      jumpToMiniApp(() => {
        eventBus.$emit('show-mini-program')
      })
    }
  }
}
</script>

<style scoped>
/* 加载样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: #1a5a36;
}

.fa-spinner {
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 导航遮罩层 */
.navigating-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
.navigating-box {
  background: #fff;
  padding: 24px 40px;
  border-radius: 12px;
  font-size: 1.1rem;
  color: #1a5a36;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
}
.navigating-box .fa-spinner {
  margin-right: 10px;
}
</style>
